<template>
  <div class="partner-program">
    <div class="page-header">
      <h2>{{ $t('legacy.Партнёрская программа') }}</h2>
    </div>

    <div v-if="loading" class="loading">
      {{ $t('common.loading') }}
    </div>

    <div v-else-if="!isAmbassador" class="not-ambassador">
      <div class="info-card">
        <h3>{{ $t('legacy.Вы пока не являетесь амбасадором') }}</h3>
        <p>{{ $t('legacy.Если вы хотите стать партнером и зарабатывать на привлечении новых пользователей, свяжитесь с администрацией.') }}</p>
      </div>
    </div>

    <div v-else class="ambassador-dashboard">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ stats.referrals_count || 0 }}</div>
          <div class="stat-label">{{ $t('legacy.Привлечённых пользователей') }}</div>
        </div>
        <div class="stat-card">
           <div class="stat-value">{{ formatMoney(stats.current_balance) }}</div>
          <div class="stat-label">{{ $t('legacy.Доступно к выводу') }}</div>
        </div>
        <div class="stat-card">
           <div class="stat-value">{{ formatMoney(stats.total_earned) }}</div>
          <div class="stat-label">{{ $t('legacy.Всего заработано') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ ambassador.commission_percent }}%</div>
          <div class="stat-label">{{ $t('legacy.Ваша комиссия') }}</div>
        </div>
      </div>

      <div class="section">
        <h3>{{ $t('legacy.Ваши промо-коды') }}</h3>
        <p class="hint">{{ $t('legacy.Поделитесь этими промо-кодами с друзьями и получайте комиссию с их оплат.') }}</p>
        
        <div v-if="promoCodes.length" class="promo-codes-list">
          <div v-for="pc in promoCodes" :key="pc.id" class="promo-card">
            <div class="promo-code-wrapper">
              <code class="promo-code">{{ pc.code }}</code>
              <button @click="copyCode(pc.code)" class="btn-copy">{{ $t('legacy.Копировать') }}</button>
            </div>
            <div class="promo-details">
              <span v-if="pc.type === 'discount'" class="promo-type discount">
                {{ $t('legacy.dynamic.discountPromo', { percent: pc.discount_percent }) }}
              </span>
              <span v-else class="promo-type trial">
                {{ $t('legacy.dynamic.trialPromo', {
                  days: pc.trial_days,
                  plan: pc.trial_plan === 'base' ? $t('legacy.Базовый') : $t('legacy.Премиум')
                }) }}
              </span>
              <span class="promo-usages">
                {{ $t('legacy.Использован:') }} {{ pc.current_usages }} {{ pc.max_usages ? `/ ${pc.max_usages}` : '' }}
              </span>
              <span v-if="pc.expires_at" class="promo-expires">
                {{ $t('legacy.До') }} {{ formatDate(pc.expires_at) }}
              </span>
            </div>
          </div>
        </div>
        <p v-else class="no-data">{{ $t('legacy.Промо-кодов пока нет. Обратитесь к администратору для их создания.') }}</p>
      </div>

      <div class="section">
        <h3>{{ $t('legacy.Привлечённые пользователи') }}</h3>
        <table v-if="referrals.length" class="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>{{ $t('legacy.Имя') }}</th>
              <th>{{ $t('legacy.Платежей') }}</th>
              <th>{{ $t('legacy.Дата привязки') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ref in referrals" :key="ref.id">
              <td>{{ ref.email }}</td>
              <td>{{ ref.name || '-' }}</td>
              <td>{{ ref.payments_count }}</td>
              <td>{{ formatDate(ref.created_at) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-data">{{ $t('legacy.Пока никто не использовал ваши промо-коды.') }}</p>
      </div>

      <div class="section">
        <h3>{{ $t('legacy.История комиссий') }}</h3>
        <table v-if="commissions.length" class="data-table">
          <thead>
            <tr>
              <th>{{ $t('legacy.Сумма') }}</th>
              <th>{{ $t('legacy.От платежа') }}</th>
              <th>{{ $t('legacy.Дата') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="com in commissions" :key="com.id">
               <td>{{ formatMoney(com.amount) }}</td>
               <td>{{ formatMoney(com.payment_amount) }}</td>
              <td>{{ formatDate(com.created_at) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-data">{{ $t('legacy.История комиссий пуста.') }}</p>
      </div>

      <div class="section">
        <h3>{{ $t('legacy.История выплат') }}</h3>
        <table v-if="payouts.length" class="data-table">
          <thead>
            <tr>
              <th>{{ $t('legacy.Сумма') }}</th>
              <th>{{ $t('legacy.Комментарий') }}</th>
              <th>{{ $t('legacy.Дата') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pay in payouts" :key="pay.id">
               <td>{{ formatMoney(pay.amount) }}</td>
              <td>{{ pay.note || '-' }}</td>
              <td>{{ formatDate(pay.created_at) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-data">{{ $t('legacy.Выплат пока не было.') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getMyAmbassadorData } from '@/api/ambassadors';
import { useToast } from 'vue-toastification';

export default {
  name: 'PartnerProgram',
  data() {
    return {
      loading: true,
      isAmbassador: false,
      ambassador: null,
      stats: {},
      promoCodes: [],
      referrals: [],
      payouts: [],
      commissions: [],
      toast: null
    };
  },
  async created() {
    this.toast = useToast();
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        const data = await getMyAmbassadorData();
        this.isAmbassador = true;
        this.ambassador = data.ambassador;
        this.stats = data.stats || {};
        this.promoCodes = data.promoCodes || [];
        this.referrals = data.referrals || [];
        this.payouts = data.payouts || [];
        this.commissions = data.commissions || [];
      } catch (error) {
        if (error.response?.status === 404) {
          this.isAmbassador = false;
        } else {
          console.error('Error loading partner data:', error);
        }
      } finally {
        this.loading = false;
      }
    },
    formatMoney(value) {
      return this.$formatCurrency(parseFloat(value || 0), 'RUB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return this.$formatDate(new Date(date));
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code);
         this.toast.success(this.$t('legacy.Промо-код скопирован!'));
      } catch (error) {
         this.toast.error(this.$t('legacy.Не удалось скопировать'));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/global.scss";

.partner-program {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;
  
  h2 {
    margin: 0;
    color: #1F1E29;
  }
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.not-ambassador {
  .info-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 32px;
    text-align: center;
    
    h3 {
      margin: 0 0 12px 0;
      color: #1F1E29;
    }
    
    p {
      color: #666;
      margin: 0;
    }
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  text-align: center;
  
  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #BC9CFF;
  }
  
  .stat-label {
    font-size: 13px;
    color: #666;
    margin-top: 6px;
  }
}

.section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 20px;
  
  h3 {
    margin: 0 0 8px 0;
    color: #1F1E29;
  }
  
  .hint {
    color: #888;
    margin: 0 0 16px 0;
    font-size: 14px;
  }
}

.promo-codes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.promo-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.promo-code-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.promo-code {
  background: #BC9CFF;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
}

.btn-copy {
  background: white;
  border: 1px solid #ddd;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #f5f5f5;
  }
}

.promo-details {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
}

.promo-type {
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  
  &.discount {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  &.trial {
    background: #e3f2fd;
    color: #1565c0;
  }
}

.promo-usages, .promo-expires {
  color: #666;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
    color: #666;
  }
}

.no-data {
  text-align: center;
  color: #999;
  padding: 24px;
}

@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .promo-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
